import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {Avatar} from '@mui/material'
import React from 'react'
import {Accordion} from '../../webui/Accordion'
import {AccordionDetails} from '../../webui/AccordionDetails'
import {AccordionSummary} from '../../webui/AccordionSummary'
import {Box} from '../../webui/Box'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {Typography} from '../../webui/Typography'
import {useAllProducts} from '../product/gql/useAllProducts'
import {getProductImageUrl} from '../../common/product/getProductImageUrl'
import {useAllOrganisations} from '../organisation/gql/useAllOrganisations'
import {FilterConfig, FilterOption, FilterValue, TableFilter} from './TableFilter'
import {GQL_ContractType} from '../../generated/graphql'
import {CONTRACT_TYPE_ORDER, contractTypeLabel} from '../../common/domain/contractType'

type ProductStatus = 'Ok' | 'MissingParts' | 'Dirty' | 'Broken' | 'Lost' | 'Other'

export type CompartmentThatNeedsActionFilterState = {
  [K in keyof CompartmentsThatNeedsActionFilter]?: FilterValue<CompartmentsThatNeedsActionFilter[K]>
}

export interface CompartmentsThatNeedsActionFilter {
  boxName: string
  endDate: string
  fromDate: string
  locked: boolean | null
  organisations: string[]
  products: string[]
  productStatuses: ProductStatus[]
  contractTypes: GQL_ContractType[]
}

interface Props {
  filter: CompartmentThatNeedsActionFilterState
  onClearFilter?: () => void
  setFilter: React.Dispatch<React.SetStateAction<CompartmentThatNeedsActionFilterState>>
}

const createFilterConfig: (
  productsOptions: FilterOption[],
  organisationOptions: FilterOption[],
) => FilterConfig<CompartmentsThatNeedsActionFilter> = (productsOptions, organisationOptions) => ({
  sectionBox: {
    label: undefined,
    fields: {
      boxName: {label: 'Box Name', type: 'string'},
    },
  },
  sectionOrganisations: {
    label: undefined,
    fields: {
      organisations: {
        label: 'Organisations',
        type: 'multiSelect',
        options: organisationOptions,
      },
    },
  },
  sectionLocked: {
    label: undefined,
    fields: {
      locked: {label: 'Locked?', type: 'boolean'},
    },
  },
  sectionDate: {
    label: 'Dates',
    fields: {
      fromDate: {label: 'From', type: 'date'},
      endDate: {label: 'To', type: 'date'},
    },
  },
  sectionContractTypes: {
    label: undefined,
    fields: {
      contractTypes: {
        label: 'Contract types',
        type: 'multiSelect',
        options: CONTRACT_TYPE_ORDER.map((t) => ({
          label: contractTypeLabel(t),
          value: t,
        })),
      },
    },
  },
  sectionProducts: {
    label: undefined,
    fields: {
      products: {
        label: 'Products',
        type: 'multiSelect',
        options: productsOptions,
      },
    },
  },
  sectionProductStatuses: {
    label: undefined,
    fields: {
      productStatuses: {
        label: 'Product Statuses',
        type: 'multiSelect',
        options: [
          {label: 'Ok', value: 'Ok'},
          {label: 'Missing Parts', value: 'MissingParts'},
          {label: 'Dirty', value: 'Dirty'},
          {label: 'Broken', value: 'Broken'},
          {label: 'Lost', value: 'Lost'},
          {label: 'Other', value: 'Other'},
        ],
      },
    },
  },
})

export const ActionPageFilter: React.FC<Props> = ({onClearFilter, filter, setFilter}) => {
  const {data: products, loading: productsLoading} = useAllProducts()
  const {data: organisations, loading: orgsLoading} = useAllOrganisations({})

  const productsOptions: FilterOption[] = products
    .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
    .map((product) => ({
      label: (
        <Box display="flex" flexDirection="row" alignItems={'center'}>
          <Avatar alt={product.name} src={getProductImageUrl(product.imageName)} style={{marginRight: 8}} />
          <Box ml={1} />
          <Typography>{product.name}</Typography>
        </Box>
      ),
      value: product.id,
    }))

  const organisationOptions: FilterOption[] = organisations
    .sort((a, b) => a.name.localeCompare(b.name, 'sv'))
    .map((org) => ({
      label: org.name,
      value: org.id,
    }))

  if (productsLoading || orgsLoading) {
    return <LoadingSpinnerContainer />
  }

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="filter-content" id="filter-header">
        <Typography variant="h5">Filter</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TableFilter
          filtersConfig={createFilterConfig(productsOptions, organisationOptions)}
          filters={filter}
          onClearFilter={onClearFilter}
          onFilterChange={setFilter}
        />
      </AccordionDetails>
    </Accordion>
  )
}
